import {
  ITrack,
  ITrackLookupArtist,
  ITrackLookupResult,
  TrackType,
} from '@/data/models/track';
import { IArtist } from '@/data/models/artist';
import { IGenre } from '@/data/models/genre';
import { isVideoFormat } from '@/utils/helper';
import { nonEmptyStringOrExisting, nonEmptyArrayOrExisting } from './util';

const DEFAULT_ARTIST_ROLE = 'Main';

export default function copyLookupMetadataToTrack(
  track: ITrack,
  selectedTrack: ITrackLookupResult,
  prsGenres: IGenre[] = [],
  targetFormatName?: string | null
): ITrack {
  const genre = resolveLookupGenre(track.genre, selectedTrack, prsGenres);
  const artists = resolveLookupArtists(track.artists, selectedTrack.artists);
  const trackType = resolveLookupTrackType(
    track.trackType,
    selectedTrack,
    targetFormatName
  );

  return {
    ...track,
    isrc: nonEmptyStringOrExisting(selectedTrack.isrc, track.isrc),
    title: nonEmptyStringOrExisting(selectedTrack.trackTitle, track.title),
    displayArtist: nonEmptyStringOrExisting(
      selectedTrack.displayArtist,
      track.displayArtist
    ),
    version: nonEmptyStringOrExisting(selectedTrack.version, track.version),
    duration: selectedTrack.duration ?? track.duration,
    copyrightYear: selectedTrack.copyrightYear ?? track.copyrightYear,
    copyrightLine: nonEmptyStringOrExisting(
      selectedTrack.copyrightLine,
      track.copyrightLine
    ),
    phonographicCopyrightYear:
      selectedTrack.phonographicCopyrightYear ??
      track.phonographicCopyrightYear,
    phonographicCopyrightLine: nonEmptyStringOrExisting(
      selectedTrack.phonographicCopyrightLine,
      track.phonographicCopyrightLine
    ),
    genre,
    audioLanguage: nonEmptyStringOrExisting(
      selectedTrack.audioLanguage,
      track.audioLanguage
    ),
    trackType,
    artists,
    writersComposers: nonEmptyArrayOrExisting(
      selectedTrack.contributors,
      track.writersComposers
    ),
    publishers: nonEmptyArrayOrExisting(
      selectedTrack.publishers,
      track.publishers
    ),
  };
}

function resolveLookupTrackType(
  currentTrackType: TrackType | null | undefined,
  selectedTrack: ITrackLookupResult,
  targetFormatName?: string | null
): TrackType | null | undefined {
  if (targetFormatName === undefined) {
    return currentTrackType;
  }

  if (!isVideoFormat(targetFormatName)) {
    return null;
  }

  if (selectedTrack.source?.toLowerCase() !== 'physical') {
    return currentTrackType;
  }

  if (!isVideoFormat(selectedTrack.productType)) {
    return TrackType.VideoAndAudio;
  }

  return selectedTrack.trackType ?? currentTrackType ?? TrackType.VideoAndAudio;
}

function resolveLookupArtists(
  currentArtists: IArtist[] | undefined,
  lookupArtists: ITrackLookupArtist[] | null | undefined
): IArtist[] | undefined {
  if (!lookupArtists?.length) {
    return currentArtists;
  }

  const artists = lookupArtists.reduce<IArtist[]>(
    (copiedArtists, lookupArtist, index) => {
      const currentArtist = currentArtists?.[index];
      const name = nonEmptyStringOrExisting(
        lookupArtist.name,
        currentArtist?.name
      );

      if (!name) {
        return copiedArtists;
      }

      copiedArtists.push({
        ...currentArtist,
        name,
        performerRole:
          nonEmptyStringOrExisting(
            lookupArtist.performerRole,
            currentArtist?.performerRole
          ) ?? DEFAULT_ARTIST_ROLE,
        sequence: lookupArtist.sequence ?? currentArtist?.sequence ?? index,
      });

      return copiedArtists;
    },
    []
  );

  return artists.length > 0 ? artists : currentArtists;
}

function resolveLookupGenre(
  currentGenre: IGenre | undefined,
  selectedTrack: ITrackLookupResult,
  prsGenres: IGenre[]
): IGenre | undefined {
  if (!selectedTrack.genre) {
    return currentGenre;
  }

  if (selectedTrack.source?.toLowerCase() !== 'digital') {
    return {
      ...selectedTrack.genre,
      masterGenre: selectedTrack.genre.masterGenre ?? null,
    };
  }

  return findMatchingPrsGenre(selectedTrack.genre, prsGenres) ?? currentGenre;
}

function findMatchingPrsGenre(
  lookupGenre: IGenre,
  prsGenres: IGenre[]
): IGenre | undefined {
  const lookupName = normalizeValue(lookupGenre.name);
  const lookupMasterGenre = normalizeValue(lookupGenre.masterGenre);

  if (!lookupName) {
    return undefined;
  }

  const nameMatches = prsGenres.filter(
    (genre) => normalizeValue(genre.name) === lookupName
  );

  if (lookupMasterGenre) {
    return nameMatches.find(
      (genre) => normalizeValue(genre.masterGenre) === lookupMasterGenre
    );
  }

  return nameMatches.length === 1 ? nameMatches[0] : undefined;
}

function normalizeValue(value: string | null | undefined) {
  return value?.trim().toLowerCase();
}
